import { useState, useMemo, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowUp, ArrowDown, AlertTriangle } from 'lucide-react';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { routingPolicyService } from '../../services';
import type { RoutingPolicy, RoutingPolicyRule, ApplicationDefinition, ApplicationGroup } from '../../services';
import { useToast } from '../common/ToastContainer';
import RuleModal from './RuleModal';
import { usePermissions } from '../../hooks/usePermissions';

function isRuleIncomplete(rule: RoutingPolicyRule): boolean {
  if (rule.enabled === 0) return false;
  const needsTarget = ['route_via_interface', 'route_via_nexthop', 'route_via_tunnel'].includes(rule.action_type);
  if (needsTarget && !rule.action_target) return true;
  if (rule.action_type === 'rate_limit' && !rule.bandwidth_limit) return true;
  return false;
}

interface PolicyDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  policy: RoutingPolicy;
  applications: ApplicationDefinition[];
  appGroups?: ApplicationGroup[];
  onRefresh: () => void;
  edgeId?: string;
}

const PolicyDetailsModal = ({ isOpen, onClose, policy, applications, appGroups = [], onRefresh, edgeId }: PolicyDetailsModalProps) => {
  const toast = useToast();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RoutingPolicyRule | null>(null);
  const [rules, setRules] = useState<RoutingPolicyRule[]>(policy.rules || []);
  const [confirmState, setConfirmState] = useState<{ show: boolean; ruleId?: string }>({ show: false });
  const [actionLoading, setActionLoading] = useState(false);

  // Sync rules state when policy prop changes
  useEffect(() => {
    setRules(policy.rules || []);
  }, [policy]);

  const isDgScope = policy.scope === 'device_group' || policy.scope === 'dg_template';

  const incompleteRuleCount = useMemo(
    () => policy.edge_id ? rules.filter(isRuleIncomplete).length : 0,
    [rules, policy.edge_id]
  );

  const handleAddRule = () => {
    setEditingRule(null);
    setIsRuleModalOpen(true);
  };

  const handleEditRule = (rule: RoutingPolicyRule) => {
    setEditingRule(rule);
    setIsRuleModalOpen(true);
  };

  const handleSaveRule = async (data: any) => {
    try {
      if (editingRule) {
        const updatedRule = await routingPolicyService.updateRule(policy.id, editingRule.id, data);
        // Optimistic update: replace the edited rule in-place
        setRules(prev => prev.map(r => r.id === editingRule.id ? updatedRule : r));
        toast.success('Rule updated successfully');
      } else {
        const newRule = await routingPolicyService.addRule(policy.id, data);
        // Optimistic update: append the new rule
        setRules(prev => [...prev, newRule]);
        toast.success('Rule added successfully');
      }
      setIsRuleModalOpen(false);
      setEditingRule(null);
      onRefresh();
    } catch (error: any) {
      console.error('Failed to save rule:', error);
      const saveMsg = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to save rule';
      toast.error(typeof saveMsg === 'string' ? saveMsg : 'Failed to save rule');
    }
  };

  const handleDeleteRule = (ruleId: string) => {
    setConfirmState({ show: true, ruleId });
  };

  const confirmDeleteRule = async () => {
    if (!confirmState.ruleId) return;
    const deletedId = confirmState.ruleId;

    setActionLoading(true);
    try {
      await routingPolicyService.deleteRule(policy.id, deletedId);
      // Optimistic update: remove the deleted rule immediately
      setRules(prev => prev.filter(r => r.id !== deletedId));
      toast.success('Rule deleted successfully');
      setConfirmState({ show: false });
      onRefresh();
    } catch (error: any) {
      console.error('Failed to delete rule:', error);
      const deleteMsg = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to delete rule';
      toast.error(typeof deleteMsg === 'string' ? deleteMsg : 'Failed to delete rule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMoveRule = async (ruleId: string, direction: 'up' | 'down') => {
    const currentIndex = rules.findIndex(r => r.id === ruleId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= rules.length) return;

    // Create new order
    const newRules = [...rules];
    [newRules[currentIndex], newRules[newIndex]] = [newRules[newIndex], newRules[currentIndex]];

    // Update rule_order for all affected rules
    const ruleOrders = newRules.map((rule, index) => ({
      ruleId: rule.id,
      order: index + 1,
    }));

    try {
      await routingPolicyService.reorderRules(policy.id, ruleOrders);
      setRules(newRules);
      toast.success('Rule order updated');
      onRefresh();
    } catch (error) {
      console.error('Failed to reorder rules:', error);
      toast.error('Failed to reorder rules');
    }
  };

  const getApplicationName = (appId?: string) => {
    if (!appId) return null;
    const app = applications.find(a => a.id === appId);
    return app?.name;
  };

  const getGroupName = (groupId?: string) => {
    if (!groupId) return null;
    const group = appGroups.find(g => g.id === groupId);
    return group?.name;
  };

  const getActionColor = (actionType: string) => {
    switch (actionType) {
      case 'route_via_interface':
      case 'route_via_nexthop':
      case 'route_via_tunnel':
        return 'bg-blue-100 text-blue-700';
      case 'allow':
        return 'bg-green-100 text-green-700';
      case 'drop':
        return 'bg-red-100 text-red-700';
      case 'rate_limit':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getQoSColor = (qos?: string) => {
    switch (qos) {
      case 'realtime':
        return 'bg-red-100 text-red-700';
      case 'priority':
        return 'bg-yellow-100 text-yellow-700';
      case 'best_effort':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Policy Details: ${policy.name}`}
      size="2xl"
    >
      <div className="space-y-6">
        {/* Policy Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500">Policy Name</p>
              <p className="text-sm font-semibold text-gray-900">{policy.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Priority</p>
              <p className="text-sm font-semibold text-gray-900">{policy.priority}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Type</p>
              <p className="text-sm font-semibold text-gray-900">
                {policy.scope === 'dg_template' ? 'DG Access Template'
                  : policy.scope === 'device_group' ? 'DG Access Policy'
                  : policy.edge_id ? 'Edge-Specific' : 'Template'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Status</p>
              <p className={`text-sm font-semibold ${policy.enabled === 1 ? 'text-green-600' : 'text-gray-600'}`}>
                {policy.enabled === 1 ? 'Enabled' : 'Disabled'}
              </p>
            </div>
            {policy.description && (
              <div className="col-span-2">
                <p className="text-xs text-gray-500">Description</p>
                <p className="text-sm text-gray-900">{policy.description}</p>
              </div>
            )}
            {/* Default Action — DG scope only */}
            {isDgScope && (
              <div>
                <p className="text-xs text-gray-500">Default Action</p>
                <p className={`text-sm font-semibold ${policy.default_action === 'deny' ? 'text-red-600' : 'text-green-600'}`}>
                  {policy.default_action === 'deny' ? 'Deny (Zero Trust)' : 'Allow'}
                </p>
              </div>
            )}
            {/* Inspection Mode — hidden for DG scope */}
            {!isDgScope && (
              <div>
                <p className="text-xs text-gray-500">Inspection Mode</p>
                <p className="text-sm font-semibold text-gray-900">
                  {(policy.inspection_mode || 'auto') === 'auto' ? 'Auto' :
                    policy.inspection_mode === 'pre_inspection' ? 'Fast Path' : 'Full Inspection'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Completeness Warning Banner */}
        {incompleteRuleCount > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {incompleteRuleCount} rule{incompleteRuleCount > 1 ? 's' : ''} missing edge-specific configuration
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Configure all rules before enabling this policy. Rules need action targets, subnets, or bandwidth limits.
              </p>
            </div>
          </div>
        )}

        {/* Rules Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {isDgScope ? 'Access Rules' : 'Routing Rules'} ({rules.length})
            </h3>
            {canCreate && (
              <button
                onClick={handleAddRule}
                className="btn-primary inline-flex items-center space-x-2 text-sm py-1.5"
              >
                <Plus className="w-4 h-4" />
                <span>Add Rule</span>
              </button>
            )}
          </div>

          {rules.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No rules defined yet</p>
              <p className="text-sm text-gray-400 mt-1">
                {isDgScope
                  ? 'Add access rules to define which network resources group members can reach'
                  : 'Add rules to define how traffic should be routed'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...rules]
                .sort((a, b) => a.rule_order - b.rule_order)
                .map((rule, index) => (
                  <div
                    key={rule.id}
                    className={`rounded-lg p-4 hover:shadow-md transition-shadow ${policy.edge_id && isRuleIncomplete(rule)
                        ? 'bg-amber-50 border border-amber-300'
                        : 'bg-white border border-gray-200'
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-semibold">
                            #{rule.rule_order}
                          </span>
                          {rule.app_definition_id && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              App: {getApplicationName(rule.app_definition_id) || rule.app_definition_id}
                            </span>
                          )}
                          {rule.app_group_id && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              Group: {getGroupName(rule.app_group_id) || rule.app_group_id}
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(rule.action_type)}`}>
                            {(rule.action_type || 'unknown').replace(/_/g, ' ').toUpperCase()}
                          </span>
                          {rule.qos_profile && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getQoSColor(rule.qos_profile)}`}>
                              QoS: {(rule.qos_profile || '').toUpperCase()}
                            </span>
                          )}
                          {rule.requires_suricata === 1 && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                              Suricata
                            </span>
                          )}
                          {rule.dscp_mark !== undefined && rule.dscp_mark !== null && (
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-mono">
                              DSCP:{rule.dscp_mark}
                            </span>
                          )}
                          {rule.dscp_match && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono">
                              Match:{rule.dscp_match}
                            </span>
                          )}
                          {rule.enabled === 0 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                              DISABLED
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          {/* Source — hidden for DG (auto-injected from group CIDR) */}
                          {!isDgScope && (
                            rule.source_subnet ? (
                              <div>
                                <span className="font-medium">Source:</span> {rule.source_subnet}
                              </div>
                            ) : !policy.edge_id ? (
                              <div>
                                <span className="font-medium">Source:</span>{' '}
                                <span className="text-gray-400 italic">Set when applied to edge</span>
                              </div>
                            ) : null
                          )}
                          {/* Destination — show value or "Any" for DG */}
                          {rule.destination_subnet ? (
                            <div>
                              <span className="font-medium">Destination:</span> {rule.destination_subnet}
                            </div>
                          ) : isDgScope ? (
                            <div>
                              <span className="font-medium">Destination:</span>{' '}
                              <span className="text-gray-400">Any</span>
                            </div>
                          ) : !policy.edge_id ? (
                            <div>
                              <span className="font-medium">Destination:</span>{' '}
                              <span className="text-gray-400 italic">Set when applied to edge</span>
                            </div>
                          ) : null}
                          {/* Target — hidden for DG (auto-injected by batch builder) */}
                          {!isDgScope && (
                            <div>
                              <span className="font-medium">Target:</span>{' '}
                              {rule.action_target ? (
                                rule.action_target
                              ) : ['route_via_interface', 'route_via_nexthop', 'route_via_tunnel'].includes(rule.action_type) ? (
                                <span className="text-gray-400 italic">Set when applied to edge</span>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </div>
                          )}
                          {rule.time_window && (
                            <div>
                              <span className="font-medium">Schedule:</span> {rule.time_window}
                            </div>
                          )}
                          {rule.bandwidth_limit && (
                            <div>
                              <span className="font-medium">Bandwidth Limit:</span> {rule.bandwidth_limit} Mbps
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-1 ml-4">
                        {canUpdate && (
                          <>
                            <button
                              onClick={() => handleMoveRule(rule.id, 'up')}
                              disabled={index === 0}
                              className="p-1 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move Up"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleMoveRule(rule.id, 'down')}
                              disabled={index === rules.length - 1}
                              className="p-1 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move Down"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditRule(rule)}
                              className="p-1 hover:bg-gray-100 rounded text-primary-600"
                              title="Edit Rule"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="p-1 hover:bg-gray-100 rounded text-red-600"
                            title="Delete Rule"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-400 p-3">
          <p className="text-sm text-amber-700">
            <strong>Note:</strong> Rules are evaluated in order from top to bottom. First matching rule wins.
            {isDgScope && policy.default_action === 'deny' && ' Unmatched traffic is blocked (Zero Trust).'}
            {isDgScope && policy.default_action !== 'deny' && ' Unmatched traffic is allowed.'}
          </p>
        </div>
      </div>

      {/* Rule Modal */}
      <RuleModal
        isOpen={isRuleModalOpen}
        onClose={() => { setIsRuleModalOpen(false); setEditingRule(null); }}
        onSave={handleSaveRule}
        rule={editingRule}
        applications={applications}
        appGroups={appGroups}
        mode={editingRule ? 'edit' : 'add'}
        isTemplate={!policy.edge_id}
        edgeId={edgeId}
        policyScope={policy.scope}
        tenantId={policy.tenant_id}
        defaultAction={policy.default_action}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmState.show}
        onClose={() => setConfirmState({ show: false })}
        onConfirm={confirmDeleteRule}
        title="Delete Rule"
        message="Are you sure you want to delete this rule?"
        confirmText="Delete"
        variant="danger"
        isLoading={actionLoading}
      />
    </Modal>
  );
};

export default PolicyDetailsModal;
